import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import DoctorCalendar from "../../components/DoctorCalendar";
import ConfirmationModal from "../../components/ConfirmationModal";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import { makeStyles } from "@material-ui/core/styles";
import {
	Paper,
	Typography,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Button,
	FormHelperText,
	Grid,
	Box,
	CircularProgress
} from "@material-ui/core";
import api from "../../services/api";
import { toast } from "react-toastify";
import { useLocation } from "react-router-dom";
import { useTitle } from "../../hooks/useTitle";

const useStyles = makeStyles((theme) => ({
	rootContainer: {
		height: "calc(100vh - 100px)",
		display: "flex",
		flexDirection: "column",
		padding: theme.spacing(2),
		boxSizing: "border-box",
	},
	mainPaper: {
		padding: theme.spacing(2),
		borderRadius: theme.shape.borderRadius,
		backgroundColor: theme.palette.background.paper,
		boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
		flexGrow: 1,
		display: "flex",
		flexDirection: "column",
		overflow: "hidden",
	},
	mainHeader: {
		padding: theme.spacing(3),
		borderBottom: "1px solid " + theme.palette.divider,
		backgroundColor: theme.palette.background.paper,
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		flexWrap: "wrap",
		gap: theme.spacing(2),
	},
	headerTitleContainer: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		flexGrow: 1,
	},
	headerActions: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(2),
		flexShrink: 0,
	},
	googleButton: {
		display: "flex",
		alignItems: "center",
		backgroundColor: "#fff",
		color: "#3c4043",
		border: "1px solid #dadce0",
		textTransform: "none",
		fontWeight: 500,
		padding: "6px 16px",
		borderRadius: 4,
		boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
		transition: "all 0.2s",
		"& img": {
			marginRight: theme.spacing(1),
		},
		"&:hover": {
			backgroundColor: "#f7f8f8",
			borderColor: "#d2e3fc",
			boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
		},
	},
	googleButtonActive: {
		backgroundColor: "#e8f0fe",
		color: "#1967d2",
		borderColor: "#d2e3fc",
		"&:hover": {
			backgroundColor: "#d2e3fc",
		}
	},
	filterContainer: {
		marginBottom: theme.spacing(1),
		padding: theme.spacing(1),
		backgroundColor: theme.palette.background.default,
		borderRadius: theme.shape.borderRadius,
		border: "1px solid " + theme.palette.divider,
	},
	calendarContainer: {
		flexGrow: 1,
		marginTop: theme.spacing(1),
	}
}));

const Calendar = () => {
	const classes = useStyles();
	const { user } = useContext(AuthContext);
	const location = useLocation();

	const [doctors, setDoctors] = useState([]);
	const [selectedDoctor, setSelectedDoctor] = useState("");
	const [selectedDoctorName, setSelectedDoctorName] = useState("");
	const [doctorTitles, setDoctorTitles] = useState([]);
	const [selectedTitleId, setSelectedTitleId] = useState(null);
	const [errorEspecialidad, setErrorEspecialidad] = useState(false);
	
	const [googleEnabled, setGoogleEnabled] = useState(false);
	const [loadingGoogle, setLoadingGoogle] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	useTitle(selectedDoctorName ? "Calendario de " + selectedDoctorName : "Calendario");

	useEffect(() => {
		if (user && user.profile !== "doctor") {
			api.get("/users", { params: { profile: "doctor" } })
				.then(({ data }) => {
					setDoctors(data.users);
				});
		} else if (user) {
			setSelectedDoctor(user.id);
			setSelectedDoctorName(user.name);
		}
	}, [user]);

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const googleConnected = params.get("googleConnected");

		if (googleConnected === "true") {
			toast.success("Google Calendar conectado correctamente");
			window.history.replaceState({}, document.title, location.pathname);
			setGoogleEnabled(true);
		}
	}, [location]);

	useEffect(() => {
		const fetchDoctorData = async () => {
			if (!selectedDoctor) return;
			
			if (user.profile !== "doctor") {
				const doc = doctors.find(d => d.id === selectedDoctor);
				if (doc) setSelectedDoctorName(doc.name);
			}

			try {
				const userRes = await api.get("/users/" + selectedDoctor);
				const titles = userRes.data.professionalTitles || [];
				setDoctorTitles(titles);
				
				if (titles.length === 1) {
					setSelectedTitleId(titles[0].id);
				} else {
					setSelectedTitleId(null);
				}
						
				if (user && user.profile === "doctor") {
					setLoadingGoogle(true);
					try {
						const googleRes = await api.get("/doctors/" + selectedDoctor + "/googleCalendar");
						const setting = googleRes.data;
						const isConnected = setting && setting.enabled && new Date(setting.tokenExpiry) > new Date();
						setGoogleEnabled(isConnected);
					} catch (googleErr) {
						if (googleErr.response && googleErr.response.status === 404) {
							setGoogleEnabled(false);
						} else {
							console.error("Error checking google calendar", googleErr);
						}
					} finally {
						setLoadingGoogle(false);
					}
				}
			} catch (err) {
				console.error("Error cargando datos de doctor", err);
			}
		};

		fetchDoctorData();
	}, [selectedDoctor, user, doctors]);

	useEffect(() => {
		(selectedTitleId) ? setErrorEspecialidad(false) : setErrorEspecialidad(true);
	}, [selectedTitleId]);
				
	const handleGoogleConnect = async () => {
		setLoadingGoogle(true);
		try {
			const res = await api.post("/doctors/" + selectedDoctor + "/googleCalendar");
			const redirectUrl = res.data?.url;

			if (redirectUrl) {
				window.location.href = redirectUrl;
			} else {
				setGoogleEnabled(true);
				toast.success("Conectado a Google Calendar");
				setLoadingGoogle(false);
			}
		} catch (err) {
			console.error(err);
			toast.error("Error al conectar Google Calendar");
			setLoadingGoogle(false);
		}
	};

	const handleGoogleDisconnect = async () => {
		setLoadingGoogle(true);
		try {
			await api.put("/doctors/" + selectedDoctor + "/googleCalendar/disable");
			setGoogleEnabled(false);
			toast.success("Integración con Google Calendar desactivada");
		} catch (err) {
			console.error("Error desconectando Google", err);
			toast.error("Error al desconectar Google Calendar");
		} finally {
			setLoadingGoogle(false);
		}
	};

	const GoogleButton = () => {
		if (loadingGoogle) {
			return (
				<Button className={classes.googleButton} disabled>
					<CircularProgress size={20} style={{ marginRight: 8 }} />
					Verificando...
				</Button>
			);
		}

		return !googleEnabled ? (
			<Button className={classes.googleButton} onClick={() => setConfirmOpen(true)}>
				<img src="https://www.gstatic.com/images/branding/product/1x/calendar_48dp.png" alt="Google Calendar" width="20" height="20" />
				Conectar Google Calendar
			</Button>
		) : (
			<Button 
				className={classes.googleButton + " " + classes.googleButtonActive} 
				onClick={handleGoogleDisconnect}
			>
				<img src="https://www.gstatic.com/images/branding/product/1x/calendar_48dp.png" alt="Google Calendar" width="20" height="20" />
				Sincronizado (Desactivar)
			</Button>
		);
	};

	return (
		<MainContainer>
			<MainHeader className={classes.mainHeader}> 
				<div className={classes.headerTitleContainer}>
					<Title>{selectedDoctorName ? "Agenda de " + selectedDoctorName : "Calendario General"}</Title>
				</div>

				<div className={classes.headerActions}>
					{user?.profile === "doctor" && <GoogleButton />}
				</div>
			</MainHeader>

			<div className={classes.rootContainer}> 
				<Paper className={classes.mainPaper} variant="outlined">
				
					{(user?.profile !== "doctor" || doctorTitles.length > 1) && (
						<div className={classes.filterContainer}>
							<Grid container spacing={2} alignItems="center">

								{user?.profile !== "doctor" && (
									<Grid item xs={12} sm={6} md={4}>
										<FormControl variant="outlined" size="small" fullWidth>
											<InputLabel>Seleccionar Doctor</InputLabel>
											<Select
												value={selectedDoctor}
												onChange={(e) => setSelectedDoctor(e.target.value)}
												label="Seleccionar Doctor"
												MenuProps={{
													getContentAnchorEl: null,
													anchorOrigin: { vertical: "bottom", horizontal: "left" },
													transformOrigin: { vertical: "top", horizontal: "left" }
												}}
											>
												{doctors.map((doc) => (
													<MenuItem key={doc.id} value={doc.id}>
														{doc.name}
													</MenuItem>
												))}
											</Select>
										</FormControl>
									</Grid>
								)}

								{doctorTitles.length > 1 && (
									<Grid item xs={12} sm={6} md={4}>
										<FormControl variant="outlined" size="small" fullWidth error={errorEspecialidad && !!selectedDoctor}>
											<InputLabel>Especialidad</InputLabel>
											<Select
												value={selectedTitleId || ""}
												onChange={(e) => setSelectedTitleId(e.target.value)}
												label="Especialidad"
												MenuProps={{
													getContentAnchorEl: null,
													anchorOrigin: { vertical: "bottom", horizontal: "left" },
													transformOrigin: { vertical: "top", horizontal: "left" }
												}}
											>
												{doctorTitles.map((t) => (
													<MenuItem key={t.id} value={t.id}>
														{t.title}
													</MenuItem>
												))}
											</Select>
											{errorEspecialidad && selectedDoctor && (
												<FormHelperText>Requerido para asignar citas</FormHelperText>
											)}
										</FormControl>
									</Grid>
								)}

								{doctorTitles.length === 1 && (
									<Grid item xs={12} sm={6} md={4}>
										 <Box display="flex" alignItems="center" height="100%" pl={1}>
												<Typography variant="body2" color="textSecondary">
													<strong>Especialidad:</strong> {doctorTitles[0].title}
												</Typography>
										 </Box>
									</Grid>
								)}
							</Grid>
						</div>
					)}

					{selectedDoctor ? (
						<div className={classes.calendarContainer}>
							<DoctorCalendar
								doctorId={selectedDoctor}
								companyId={user?.companyId}
								allowAssignWithoutTicket={true}
								selectedTitleId={selectedTitleId}
							/>
						</div>
					) : (
						<Box display="flex" justifyContent="center" alignItems="center" height="400px" flexDirection="column">
							<Typography variant="h6" color="textSecondary" gutterBottom>
								Selecciona un profesional para ver su agenda
							</Typography>
							<Typography variant="body2" color="textSecondary">
								Utilice el selector superior para cargar la agenda del profesional.
							</Typography>
						</Box>
					)}
				</Paper>
			</div>
			<ConfirmationModal
				title="Conectar con Google Calendar"
				open={confirmOpen}
				onClose={() => setConfirmOpen(false)}
				onConfirm={() => {
					setConfirmOpen(false);
					handleGoogleConnect();
				}}
			>
				<Typography>
					Al conectar su cuenta de <b>Google Calendar</b>, el sistema podrá 
					crear y sincronizar eventos automáticamente. Asegúrese de iniciar sesión 
					con la cuenta correcta.
					<br /><br />
					Podrá desconectarla en cualquier momento.
				</Typography>
			</ConfirmationModal>
		</MainContainer>
	);
};

export default Calendar;